import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';
import type { Permissions } from '@repo/types/globals';
import type { ReactNode } from 'react';

import { getIsFSEntityFile } from '@repo/lib/fs/getIsFSEntityFile';
import { DownloadIcon, FileEditIcon, FolderSymlinkIcon, InfoIcon, LinkIcon, Share2Icon, Trash2Icon } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { ContextMenuContent, ContextMenuGroup, ContextMenuItem, ContextMenuLabel, ContextMenuSeparator } from '@/components/ui/context-menu';
import { DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useCopyToClipboard } from '@/hooks/copy-to-clipboard';
import { useFilesStorage } from '@/store/files-store';

interface FSEntitiesMenuProps {
  element?: FilePublicWithPermissions | DirectoryPublic;
  type: 'file' | 'directory';
  showContextMenu: boolean;
  showDropdownMenu: boolean;
  visibility?: {
    editName?: boolean;
    share?: boolean;
    download?: boolean;
    move?: boolean;
    moreInfo?: boolean;
    delete?: boolean;
    copyLink?: boolean;
  };
}

interface MenuItem {
  label: string;
  icon: ReactNode;
  action: () => void;
  visible: boolean;
  disable?: boolean;
  variant?: 'destructive' | 'default';
}

const defaultVisibility = {
  editName: true,
  share: true,
  download: true,
  move: true,
  moreInfo: true,
  delete: true,
  copyLink: true,
};

export default function FSEntitiesMenu(props: FSEntitiesMenuProps) {
  const { element, showContextMenu, showDropdownMenu } = props;

  const visibility = useMemo(() => ({ ...defaultVisibility, ...props.visibility }), [props.visibility]);
  const permissions = useMemo<Permissions | undefined>(() => element?.permissions, [element]);
  const isFile = getIsFSEntityFile(element);

  const navigate = useNavigate();
  const [, copy] = useCopyToClipboard();
  const { selectedSize, setOpenEditName, setOpenShare, setOpenDownload, setOpenMove, setOpenMoreInfo, setOpenDelete } = useFilesStorage();

  const size = selectedSize();

  const menuItems = useMemo<MenuItem[]>(() => {
    if (element === undefined || permissions === undefined) {
      return [];
    }

    return [
      {
        label: 'Zmień nazwę',
        icon: <FileEditIcon />,
        action: () => setOpenEditName(true),
        visible: visibility.editName,
        disable: !permissions.edit || size > 1,
      },
      {
        label: 'Udostępnij',
        icon: <Share2Icon />,
        action: () => setOpenShare(true),
        visible: visibility.share,
        disable: !permissions.edit,
      },
      {
        label: 'Pobierz',
        icon: <DownloadIcon />,
        action: () => setOpenDownload(true),
        visible: visibility.download,
      },
      {
        label: 'Przenieś',
        icon: <FolderSymlinkIcon />,
        action: () => setOpenMove(true),
        visible: visibility.move,
        disable: !permissions.edit,
      },
      {
        label: 'Więcej informacji',
        icon: <InfoIcon />,
        action: () => (isFile ? void navigate(`/fs/file/${element.id}`) : setOpenMoreInfo(true)),
        visible: visibility.moreInfo,
        disable: size > 1,
      },
      {
        label: 'Kopiuj link',
        icon: <LinkIcon />,
        action: () => {
          void copy(`${window.location.origin}/fs/file/${element.id}`)
            .then(() => {
              toast.success('Link skopiowany do schowka');
            })
            .catch(() => {
              toast.error('Nie udało się skopiować linku');
            });
        },
        visible: visibility.copyLink && isFile,
      },
      {
        label: 'Usuń',
        icon: <Trash2Icon />,
        action: () => setOpenDelete(true),
        visible: visibility.delete,
        disable: isFile ? !permissions.write : !permissions.edit,
        variant: 'destructive',
      },
    ];
  }, [
    copy,
    element,
    isFile,
    permissions,
    setOpenDelete,
    setOpenDownload,
    setOpenEditName,
    setOpenMoreInfo,
    setOpenMove,
    setOpenShare,
    size,
    visibility.copyLink,
    visibility.delete,
    visibility.download,
    visibility.editName,
    visibility.moreInfo,
    visibility.move,
    visibility.share,
  ]);

  if (element === undefined || permissions === undefined) {
    return null;
  }

  return (
    <>
      {showDropdownMenu ? (
        <DropdownMenuContent align={'end'} className="w-fit">
          <DropdownMenuLabel>{isFile ? 'Ustawienia Pliku' : 'Ustawienia Folderu'}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {menuItems.map((item) =>
              item.visible ? (
                <DropdownMenuItem key={item.label} disabled={item.disable} onClick={item.action} variant={item.variant}>
                  {item.icon}
                  {item.label}
                </DropdownMenuItem>
              ) : null
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      ) : null}
      {showContextMenu ? (
        <ContextMenuContent>
          <ContextMenuLabel>{isFile ? 'Ustawienia Pliku' : 'Ustawienia Folderu'}</ContextMenuLabel>
          <ContextMenuSeparator />
          <ContextMenuGroup>
            {menuItems.map((item) =>
              item.visible ? (
                <ContextMenuItem key={item.label} disabled={item.disable} onClick={item.action} variant={item.variant}>
                  {item.icon}
                  {item.label}
                </ContextMenuItem>
              ) : null
            )}
          </ContextMenuGroup>
        </ContextMenuContent>
      ) : null}
    </>
  );
}
